// src/engine/mock.ts — the default engine: fully interactive, no API key required.
// Ported from LLABARO.jsx MockEngine. Scripted listen stream + a small dictionary for
// talk-mode translation + the browser's own voices as a TTS stand-in.
import type { LangCode, LanguagePair } from "../core/models";
import type {
  AudioClip,
  ListenCallbacks,
  ListenHandle,
  TranscribeTranslateResult,
  TranslationEngine,
} from "./types";

// Scripted foreign utterances for the mock listen stream (DE<->EN travel scene).
const DEMO_SCRIPT: Record<string, { src: string; tgt: string }[]> = {
  de: [
    { src: "Guten Tag, kann ich Ihnen helfen?", tgt: "Good afternoon, can I help you?" },
    { src: "Der Zug fährt in zehn Minuten.", tgt: "The train leaves in ten minutes." },
    { src: "Das macht zusammen zwölf Euro.", tgt: "That comes to twelve euros in total." },
    { src: "Möchten Sie einen Kaffee dazu?", tgt: "Would you like a coffee with that?" },
    { src: "Der Bahnhof ist gleich um die Ecke.", tgt: "The station is just around the corner." },
  ],
  en: [
    { src: "Good afternoon, can I help you?", tgt: "Guten Tag, kann ich Ihnen helfen?" },
    { src: "The train leaves in ten minutes.", tgt: "Der Zug fährt in zehn Minuten." },
  ],
};

// Small canned dictionary so talk-mode translation looks credible without a key.
const MOCK_DICT: Record<string, Record<string, string>> = {
  "en>de": {
    "yes, where is the station?": "Ja, wo ist der Bahnhof?",
    "how much is this?": "Wie viel kostet das?",
    "thank you very much": "Vielen Dank",
    "i would like a coffee": "Ich hätte gern einen Kaffee",
    "do you speak english?": "Sprechen Sie Englisch?",
    "one ticket please": "Eine Fahrkarte bitte",
  },
  "de>en": {
    "danke schön": "Thank you",
    "wo ist die toilette?": "Where is the toilet?",
  },
};

export class MockEngine implements TranslationEngine {
  readonly hasRealBackend = false;

  startListen(pair: LanguagePair, cb: ListenCallbacks, _opts?: unknown): ListenHandle {
    const script = DEMO_SCRIPT[pair.foreign] || DEMO_SCRIPT.de;
    let i = 0;
    let cancelled = false;
    let timers: ReturnType<typeof setTimeout>[] = [];
    const clear = () => {
      timers.forEach(clearTimeout);
      timers = [];
    };

    const playOne = () => {
      if (cancelled) return;
      const line = script[i % script.length];
      i += 1;
      const srcWords = line.src.split(" ");
      const tgtWords = line.tgt.split(" ");
      // stream the foreign words, then the translation growing a beat behind
      srcWords.forEach((_, k) => {
        timers.push(
          setTimeout(() => {
            if (cancelled) return;
            cb.onForeign(srcWords.slice(0, k + 1).join(" "));
            const tk = Math.max(
              0,
              Math.round(((k + 1) * tgtWords.length) / srcWords.length) - 1
            );
            cb.onTranslation(tgtWords.slice(0, tk + 1).join(" "));
          }, 180 * (k + 1))
        );
      });
      const total = 180 * (srcWords.length + 1);
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          cb.onTranslation(line.tgt);
          cb.onFinal();
          timers.push(setTimeout(playOne, 2600)); // gap before next utterance
        }, total)
      );
    };
    timers.push(setTimeout(playOne, 800));
    return {
      stop() {
        cancelled = true;
        clear();
      },
    };
  }

  translateText(text: string, src: LangCode, dst: LangCode): Promise<string> {
    const key = (text || "").trim().toLowerCase().replace(/\s+/g, " ");
    const dict = MOCK_DICT[src + ">" + dst] || {};
    const hit = dict[key];
    // simulate network latency
    return new Promise((res) =>
      setTimeout(() => res(hit || `〔${dst}〕 ${text}`), 520 + Math.random() * 380)
    );
  }

  // Demo talk-mode has no real audio; return a canned pair so the flow stays interactive.
  async transcribeAndTranslate(
    _audio: AudioClip,
    pair: LanguagePair
  ): Promise<TranscribeTranslateResult> {
    const sourceText = "How much is this?";
    const targetText = await this.translateText(sourceText, pair.native, pair.foreign);
    return { sourceText, targetText };
  }

  // stand-in for gemini-3.1-flash-tts-preview using the browser's own voices
  speak(text: string, _voice: string, lang: LangCode, volume: number): Promise<void> {
    return new Promise((resolve) => {
      try {
        const synth = window.speechSynthesis;
        if (!synth) return resolve();
        const u = new SpeechSynthesisUtterance(text);
        u.lang =
          ({ en: "en-US", de: "de-DE", es: "es-ES", fr: "fr-FR", ja: "ja-JP" } as Record<
            string,
            string
          >)[lang] || lang;
        u.volume = Math.max(0, Math.min(1, volume));
        const match = synth.getVoices().find((v) => v.lang && v.lang.startsWith(lang));
        if (match) u.voice = match;
        u.onend = () => resolve();
        u.onerror = () => resolve();
        synth.cancel();
        synth.speak(u);
        setTimeout(resolve, 6000); // safety drain
      } catch {
        resolve();
      }
    });
  }
}
